import React, { useMemo } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useLocation, Link as RouterLink } from "react-router-dom";
import config from "../../../config/licencePriceCalculator.json";
import CustomerQuote from "./components/CustomerQuote";
import {
  calculateTotals,
  calculateInstallationTotals,
  calculateGrandTotals,
  getPartnerIdFromLocalStorage,
  isLicenseVisible,
} from "./helpers";

// Helper functions for market-specific product pricing
const getProductMocPrice = (product, market = "sk") => {
  if (!product?.price) return 0;

  // If price is a number (backward compatibility), return it
  if (typeof product.price === "number") {
    return product.price;
  }

  // If price is an object, return market-specific price
  if (typeof product.price === "object" && product.price[market]) {
    return product.price[market];
  }

  // Fallback to 'sk' if market not found
  return product.price.sk || 0;
};

const getProductVocPrice = (product, partnerId, market = "sk") => {
  if (!partnerId || !product?.partners) return null;

  // Find partner entry by matching ID (loose numeric comparison)
  for (const [key, partnerData] of Object.entries(product.partners)) {
    if (Number(partnerData.id) === Number(partnerId)) {
      // If partner price is a number (backward compatibility), return it
      if (typeof partnerData.price === "number") {
        return partnerData.price;
      }

      // If partner price is an object, return market-specific price
      if (typeof partnerData.price === "object" && partnerData.price[market]) {
        return partnerData.price[market];
      }

      // Fallback to 'sk' if market not found
      return partnerData.price.sk || null;
    }
  }

  return null;
};

const formatEuro = (val) => {
  if (val === null || val === undefined) return "—";
  const num = Number(val);
  if (Number.isNaN(num)) return "—";
  return `${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const readSnapshot = (id) => {
  if (!id) return null;
  try {
    const fromSession = sessionStorage.getItem(id);
    if (fromSession) return JSON.parse(fromSession);
  } catch {}
  try {
    const fromLocal = localStorage.getItem(id);
    if (fromLocal) return JSON.parse(fromLocal);
  } catch {}
  // Fallback to last stored quote if id lookup fails
  try {
    const last = localStorage.getItem("quote_last");
    if (last) return JSON.parse(last);
  } catch {}
  return null;
};

const PriceCalculatorQuotePage = () => {
  const q = useQuery();
  const id = q.get("id");
  const snapshot = readSnapshot(id);
  const partnerId = getPartnerIdFromLocalStorage();

  // Check if the license in the snapshot is still visible to the current user
  const licenseInSnapshot = snapshot?.selection?.licenseKey;
  const licenseConfig = config?.licenses?.[licenseInSnapshot];
  const isLicenseCurrentlyVisible = licenseInSnapshot && licenseConfig
    ? isLicenseVisible(licenseConfig, partnerId)
    : true; // If no license in snapshot or config issue, allow display

  if (!snapshot) {
    return (
      <Paper variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Ponuka sa nenašla. Vygenerujte ju znova.
          </Typography>
          <Button
            component={RouterLink}
            to="/price-calculator"
            variant="contained"
          >
            Späť na kalkulačku
          </Button>
        </Box>
      </Paper>
    );
  }

  // Check if the license in the quote is still available to the current user
  if (!isLicenseCurrentlyVisible) {
    return (
      <Paper variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Táto ponuka obsahuje typ licencie, ktorý už nie je dostupný pre váš účet.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Vygenerujte novú ponuku s dostupnými typmi licencií.
          </Typography>
          <Button
            component={RouterLink}
            to="/price-calculator"
            variant="contained"
          >
            Späť na kalkulačku
          </Button>
        </Box>
      </Paper>
    );
  }

  const selection = snapshot?.selection || {};
  const installation = snapshot?.installation || {};
  const products = snapshot?.products || {};

  // Compute MOC totals
  const totalsCustomerOnly = calculateTotals(
    {
      licenseType: selection.licenseKey,
      kioskCount: selection.kioskCount,
      commitmentOptionKey: selection.commitmentKey,
    },
    config,
    null
  );

  // Compute VOC totals if partner is logged in
  const totalsPartnerOnly = partnerId ? calculateTotals(
    {
      licenseType: selection.licenseKey,
      kioskCount: selection.kioskCount,
      commitmentOptionKey: selection.commitmentKey,
    },
    config,
    partnerId
  ) : null;

  const installationTotals = calculateInstallationTotals(
    { kioskCount: selection.kioskCount, installation },
    config
  );

  // Calculate kiosk/hardware totals
  const selectedProducts = config?.products?.filter(
    (product) => (products[product.code] || 0) > 0
  ) || [];
  const market = snapshot.productsMarket || "sk";
  const kiosksMocTotal = selectedProducts.reduce(
    (sum, product) => sum + getProductMocPrice(product, market) * (products[product.code] || 0),
    0
  );
  const kiosksVocTotal = partnerId && selectedProducts.some(product =>
    product.partners && Object.values(product.partners).some(p => Number(p.id) === Number(partnerId))
  ) ? selectedProducts.reduce((sum, product) => {
    const vocPrice = getProductVocPrice(product, partnerId, market);
    return sum + (vocPrice ? vocPrice * (products[product.code] || 0) : 0);
  }, 0) : 0;

  const grandCustomerOnly = calculateGrandTotals(
    totalsCustomerOnly,
    installationTotals
  );

  const isCz = (snapshot.productsMarket || "sk") === "cz";
  const czRate = Number(snapshot.czExchangeRate);
  const czRateValid = !Number.isNaN(czRate) && czRate > 0;
  const displayCzk =
    isCz && snapshot.displayCurrency === "Kč" && czRateValid;

  const formatPrice = (val) => {
    if (val === null || val === undefined) return "—";
    const num = Number(val);
    if (Number.isNaN(num)) return "—";
    if (displayCzk) {
      const czk = Math.round(num * czRate);
      return `${czk.toLocaleString("cs-CZ")} Kč`;
    }
    return formatEuro(val);
  };

  const quoteData = {
    snapshot: {
      licenseType: selection.licenseKey,
      kioskCount: selection.kioskCount,
      commitmentOptionKey: selection.commitmentKey,
      generatedAt: snapshot.generatedAt,
      installation,
      products: snapshot.products || {},
      productsMarket: snapshot.productsMarket || "sk",
    },
    totalsCustomerOnly,
    totalsPartnerOnly,
    installationTotals,
    kiosksMocTotal,
    kiosksVocTotal,
    grandCustomerOnly,
    formatPrice,
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1000, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
          "@media print": { display: "none" },
        }}
      >
        <Button variant="outlined" onClick={() => window.print()}>
          Tlačiť
        </Button>
        <Button component={RouterLink} to="/price-calculator">
          Späť
        </Button>
      </Box>
      <CustomerQuote data={quoteData} config={config} />
    </Box>
  );
};

export default PriceCalculatorQuotePage;
